import { useEffect, useRef, useState } from "react";
import type { Timings } from "../lib/api";

// Ordered phases with a color each. "Waiting" prefers the agent's precise
// TTFB (connection-ready → first byte); the worker only knows send → headers,
// which it reports as waitingMs.
function phasesOf(t: Timings): Array<{ key: string; label: string; ms: number; color: string }> {
  const waiting = t.ttfbMs ?? t.waitingMs;
  const rows: Array<{ key: string; label: string; ms: number | undefined; color: string }> = [
    { key: "dns", label: "DNS lookup", ms: t.dnsMs, color: "#8b5cf6" },
    { key: "tcp", label: "TCP connect", ms: t.tcpMs, color: "#6d8dff" },
    { key: "tls", label: "TLS handshake", ms: t.tlsMs, color: "#22c1c3" },
    { key: "wait", label: "Waiting (TTFB)", ms: waiting, color: "#f59e0b" },
    { key: "download", label: "Content download", ms: t.downloadMs, color: "#34d399" },
  ];
  return rows.filter((r): r is { key: string; label: string; ms: number; color: string } => typeof r.ms === "number");
}

function fmt(ms: number): string {
  if (ms < 1) return "<1 ms";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

// A clickable duration chip that reveals a per-phase timing breakdown.
export default function TimingBreakdown({ timings, durationMs }: { timings?: Timings | null; durationMs: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const phases = timings ? phasesOf(timings) : [];
  const hasBreakdown = phases.length > 0;
  const sum = phases.reduce((a, p) => a + p.ms, 0) || 1;

  if (!hasBreakdown) {
    return <span className="text-xs muted-strong mono">{durationMs} ms</span>;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="text-xs muted-strong mono hover:text-text inline-flex items-center gap-1"
        onClick={() => setOpen((v) => !v)}
        title="Show timing breakdown"
      >
        {durationMs} ms
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="opacity-60"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1.5 w-72 card shadow-[var(--shadow-strong)] p-3 space-y-2.5">
          <div className="text-[11px] uppercase tracking-wider muted-strong font-semibold">Timing</div>
          {/* Stacked bar */}
          <div className="flex h-2 rounded-full overflow-hidden bg-surface-2">
            {phases.map((p) => (
              <div key={p.key} style={{ width: `${(p.ms / sum) * 100}%`, background: p.color }} title={`${p.label}: ${fmt(p.ms)}`} />
            ))}
          </div>
          <ul className="space-y-1">
            {phases.map((p) => (
              <li key={p.key} className="flex items-center gap-2 text-xs">
                <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: p.color }} />
                <span className="flex-1 muted">{p.label}</span>
                <span className="mono">{fmt(p.ms)}</span>
              </li>
            ))}
            <li className="flex items-center gap-2 text-xs pt-1 mt-1 border-t border-border">
              <span className="h-2.5 w-2.5 shrink-0" />
              <span className="flex-1 font-medium">Total</span>
              <span className="mono font-medium">{fmt(timings!.totalMs || durationMs)}</span>
            </li>
          </ul>
          {timings && timings.dnsMs == null && (
            <p className="text-[11px] muted-strong leading-snug">
              Run through the local agent to also see DNS, TCP, and TLS timing.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
